/* The API paginates three different ways depending on the endpoint. Callers
   should never have to know which, so every list collapses to one shape. */

export const PAGE_LIMIT_MAX = 50;
export const PAGE_LIMIT_MAX_100 = 100;
export const PAGE_LIMIT_MAX_200 = 200;

/* Cursors are not one type: `nextCursor` is an opaque base64 string, while the
   `cursor` endpoints use a numeric offset. Both are carried through as-is
   rather than coerced, so neither is mangled on the way back. */
export type PageParam = string | number;

export type Page<T> = {
  items: T[];
  total: number | null;
  nextPageParam: PageParam | undefined;
};

type CursorEnvelope<T> = {
  data: T[];
  total?: number;
  nextCursor?: string | null;
  prevCursor?: string | null;
  next?: string | null;
};

/* `nextCursor`/`prevCursor` — /games, /categories/{id}/games, /promotions */
export const fromNextCursor = <T>(envelope: CursorEnvelope<T>): Page<T> => ({
  items: envelope.data,
  total: envelope.total ?? null,
  nextPageParam: envelope.nextCursor ?? undefined,
});

/* `cursor` request parameter — /categories, /bankrolls, /account/bets, /incentives/*
   The request parameter is `cursor` everywhere, but the response field is not:
   /incentives/* answers with `next` (and `previous` from page two on), which is
   an opaque base64 string. Reading only `cursor`/`nextCursor` made those
   endpoints look unpaginated, which is how /incentives/rewards ended up capped
   at one page.

   A response `cursor` is never read, because none of it is a next-page token.
   /categories is the only path in the whole document that sends one, and it
   echoes back the offset we asked for — the empty page past the end still
   answers `cursor: 10` — so reading it is an infinite loop on page one. That
   endpoint gets one clamped page and its `total`. /bankrolls takes the request
   parameter but sends no cursor back at all. */
export const fromCursor = <T>(envelope: CursorEnvelope<T>): Page<T> => {
  const cursor = envelope.nextCursor ?? envelope.next;

  return {
    items: envelope.data,
    total: envelope.total ?? null,
    nextPageParam: cursor === null || cursor === undefined ? undefined : cursor,
  };
};

/* `limit` only, no cursor — /bets, /bets/casino */
export const fromUnpaged = <T>(envelope: { data: T[]; total?: number }): Page<T> => ({
  items: envelope.data,
  total: envelope.total ?? null,
  nextPageParam: undefined,
});

/* The API rejects anything above 50 with a 400, so clamp rather than let a
   caller's page size become a runtime error. */
export const clampLimit = (limit: number, max: number = PAGE_LIMIT_MAX) =>
  Math.max(1, Math.min(Math.trunc(limit), max));
